import { Namespace, TFunction } from 'react-i18next';
import * as Yup from 'yup';
import { PROPERTIES_SELECTION } from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField.constants';
import {
  BookingDotComFeeTypeDeprecated,
  CleaningFeeCollection,
  FeeAmountTypeEnum,
  FeeConditionEnum,
  FeeScopeEnum,
  FeeTypeEnum,
} from 'models/PropertyFees';
import { InternalOptions } from 'yup/lib/types';
import {
  BOOKING_PERCENT_APPLIES_TO_OPTIONS,
  BY_LENGTH_OF_STAY_SCOPE_OPTION,
} from './PropertyFeeModal.constants';

export const propertyFeeSchema = ({ t }: { t: TFunction<Namespace<'en'>> }) =>
  Yup.object().shape({
    amount: Yup.number()
      .useNaNAsNull()
      .nullable()
      .required()
      .when(['amountType'], {
        is: FeeAmountTypeEnum.TAX,
        then: (schema) => schema.min(0).max(100),
        otherwise: (schema) => schema.min(0),
      }),
    amountType: Yup.string().oneOf(Object.values(FeeAmountTypeEnum)).required(),
    uid: Yup.string().nullable().optional(),
    name: Yup.string().required(),
    type: Yup.string().oneOf(Object.values(FeeTypeEnum)).required(),
    fee: Yup.object().when('type', {
      is: FeeTypeEnum.CUSTOM,
      then: Yup.object().shape({
        taxationRate: Yup.number()
          .min(0)
          .max(100)
          .useNaNAsNull()
          .nullable()
          .required(),
      }),
      otherwise: Yup.object().shape({}),
    }),
    tax: Yup.object().when('type', {
      is: FeeTypeEnum.TAX,
      then: Yup.object().shape({
        conditions: Yup.object().shape({
          type: Yup.string()
            .required()
            .test(
              'validate-by-night-scope',
              t(
                'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.byNightScopeValidation',
              ),
              (value, context) => {
                if (value !== FeeConditionEnum.BY_NIGHT) {
                  return true;
                }

                const rootFieldValue = (
                  context.options as InternalOptions<PropertyFeeFormSchemaType>
                ).from?.[2]?.value?.scope as FeeScopeEnum;

                return [
                  FeeScopeEnum.PER_NIGHT,
                  FeeScopeEnum.PER_GUEST_PER_NIGHT,
                ].includes(rootFieldValue);
              },
            ),
          lengthOfStayType: Yup.string().when(['type'], {
            is: BY_LENGTH_OF_STAY_SCOPE_OPTION,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.transform(() => undefined).optional(),
          }),
          lengthOfStayValue: Yup.number().when(['type'], {
            is: BY_LENGTH_OF_STAY_SCOPE_OPTION,
            then: (schema) => schema.min(1).required(),
            otherwise: (schema) => schema.transform(() => 0).optional(),
          }),
          firstNightsApplying: Yup.number().when(['type'], {
            is: FeeConditionEnum.BY_NIGHT,
            then: (schema) => schema.min(1).max(60).required(),
            otherwise: (schema) => schema.transform(() => 0).optional(),
          }),
        }),
      }),
      otherwise: Yup.object().shape({}),
    }),
    scope: Yup.string().required().oneOf(Object.values(FeeScopeEnum)),
    appliesTo: Yup.string()
      .oneOf(Object.values(PROPERTIES_SELECTION))
      .required(),
    appliesToProperties: Yup.array()
      .of(Yup.object({ label: Yup.string(), value: Yup.string() }))
      .label('properties')
      .when('appliesTo', {
        is: PROPERTIES_SELECTION.ALL_PROPERTIES,
        then: (schema) => schema.optional().nullable(true),
        otherwise: (schema) => schema.min(1).required(),
      }),
    appliesToHostfully: Yup.string().oneOf(['YES', 'NO']).required(),
    rollsIntoRent: Yup.string().oneOf(['YES', 'NO']).required(),
    optional: Yup.string().oneOf(['YES', 'NO']),
    airbnbMappingType: Yup.string().nullable(),
    airbnbUnitMappingType: Yup.string().nullable(),
    bookingMappingType: Yup.string()
      .nullable()
      .notOneOf(Object.values(BookingDotComFeeTypeDeprecated), ({ value }) => {
        return t(
          'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.channelSpecific.bookingMappingTypeValidation',
          { context: value },
        );
      }),
    hvmbMappingType: Yup.string().nullable(),
    cleaningFeeCollectionMappingType: Yup.mixed<CleaningFeeCollection>()
      .oneOf(Object.values(CleaningFeeCollection))
      .nullable(),
    vrboMappingType: Yup.string().nullable(),
    bookingPercentAppliesTo: Yup.string()
      .oneOf([...Object.values(BOOKING_PERCENT_APPLIES_TO_OPTIONS), ''])
      .nullable(),
  });

export type PropertyFeeFormSchemaType = Yup.InferType<
  ReturnType<typeof propertyFeeSchema>
>;
